<script>
import { watch, ref } from 'vue';
import pdfMake from "pdfmake/build/pdfmake";
import { pdfFileName, pdfDateRange, openPdf } from '@/utils/pdfFile';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import BalanceSheetServices from '@/services/BalanceSheetServices';

export default {
  props: {
    salesman_id: Number,
    shop_id: Number,
    count_number: Number,
    start_date: [String, Date],
    end_date: [String, Date],
  },

  setup(props) {
    const pdfData = ref([]);
    const pdfStats = ref([]);
    const documentDefinition = ref({});
    const body = ref([]);
    const salesman_name = ref('');
    const shop_name = ref('');

    const total_debit = ref(0);
    const total_return = ref(0);
    const total_credit = ref(0);
    const opening_balance = ref(0);
    const closing_remaining = ref(0);

    const loadPdf = async () => {
      await BalanceSheetServices.getshopsPdfData(
        props.shop_id,
        props.salesman_id,
        moment(props.start_date).format('YYYY-MM-DD'),
        moment(props.end_date).format('YYYY-MM-DD')
      )
        .then((response) => {
          const data = response.data.shopsheet || [];
          pdfData.value = data;

          if (data.length > 0) {
            const first = data[0];
            salesman_name.value = `${first.first_name ?? ''} ${first.last_name ?? ''}`.trim();
            shop_name.value = first.shop_name || '';
          }

          total_debit.value = 0;
          total_credit.value = 0;
          total_return.value = 0;
        })
        .then(() => {
          body.value = [];

          // Header Row
          body.value.push([
            { text: 'Date', style: 'tableHeader', alignment: 'center' },
            { text: 'Narration', style: 'tableHeader' },
            { text: 'Debit', style: 'tableHeader', alignment: 'right' },
            { text: 'Credit', style: 'tableHeader', alignment: 'right' },
            { text: 'Return', style: 'tableHeader', alignment: 'right' },
            { text: 'Balance', style: 'tableHeader', alignment: 'right' },
          ]);

          // Rows
          // Running freight total so the Balance column shows goods - freight
          // (freight is subtracted from what the shop owes).
          let cumulativeFreight = 0;
          pdfData.value.forEach((row) => {
            const dataRow = [];
            const amt = Number(row.amount ?? 0);
            const freightAmt = Number(row.freight ?? 0);

            // Date
            dataRow.push({
              text: moment(row.payment_at || row.created_at).format('DD/MM/YY'),
              style: 'cell',
              alignment: 'center',
            });

            // Description
            let descText = row.description ?? '';
            if (!descText) {
              if (row.return_id) descText = `Returned id #${row.return_id}`;
              else if (row.type === 'credit') descText = 'Amount Received';
              else if (row.type === 'debit') descText = 'Amount Deducted';
              else if (row.type === 'return') descText = 'Amount Returned';
            }
            if (row.billing_id) descText += `  Billing Id: ${row.billing_id}`;

            dataRow.push({ text: descText, style: 'cell', alignment: 'left' });

            // Debit
            let debitText = '---';
            if (row.type === 'debit') {
              const debitAmt = amt - freightAmt;
              total_debit.value += Math.abs(debitAmt);
              debitText = debitAmt.toFixed(2);
              cumulativeFreight += freightAmt;
            }
            dataRow.push({ text: debitText, style: 'numberCell', alignment: 'right' });

            // Credit
            let creditText = '---';
            if (row.type === 'credit') {
              total_credit.value += Math.abs(amt);
              creditText = Math.abs(amt).toFixed(2);
            }
            dataRow.push({ text: creditText, style: 'numberCell', alignment: 'right' });

            // Return
            let returnText = '---';
            if (row.type === 'return') {
              total_return.value += Math.abs(amt);
              returnText = Math.abs(amt).toFixed(2);
            }
            dataRow.push({ text: returnText, style: 'numberCell', alignment: 'right' });

            // Balance (stored balance less cumulative freight = net of freight)
            dataRow.push({
              text: (Number(row.remaining_amount ?? 0) - cumulativeFreight).toFixed(2),
              style: 'numberCell',
              alignment: 'right',
            });

            body.value.push(dataRow);
          });

          // Closing & Opening Calculations
          const last = pdfData.value[pdfData.value.length - 1] || null;
          closing_remaining.value = last ? Number(last.remaining_amount ?? 0) : 0;
          opening_balance.value =
            closing_remaining.value -
            total_debit.value +
            total_credit.value +
            total_return.value;

          // Totals Row
          body.value.push([
            {
              text: 'Total:',
              colSpan: 2,
              alignment: 'right',
              style: 'totalsLabel',
              fillColor: '#E5E7EB',
            },
            {},
            {
              text: total_debit.value.toFixed(2),
              style: 'numberCell',
              fillColor: '#E5E7EB',
            },
            {
              text: total_credit.value.toFixed(2),
              style: 'numberCell',
              fillColor: '#E5E7EB',
            },
            {
              text: total_return.value.toFixed(2),
              style: 'numberCell',
              fillColor: '#E5E7EB',
            },
            { text: '', style: 'numberCell', fillColor: '#E5E7EB' },
          ]);
        })
        .then(() => {
          documentDefinition.value = {
            pageMargins: [40, 50, 40, 50],
            footer: (currentPage, pageCount) => ({
              columns: [
                { text: 'Printed: ' + moment().format('LLLL'), margin: [40, 0, 0, 0] },
                {
                  text: 'Page ' + currentPage + ' of ' + pageCount,
                  alignment: 'right',
                  margin: [0, 0, 40, 0],
                },
              ],
              fontSize: 8,
            }),
            content: [
              // Title
              {
                table: {
                  widths: ['*', 'auto'],
                  body: [
                    [
                      { text: 'GENERAL LEDGER', style: 'title' },
                      {
                        text: `From: ${moment(props.start_date).format('DD/MM/YY')}   To: ${moment(props.end_date).format('DD/MM/YY')}`,
                        alignment: 'right',
                        style: 'subHeader',
                      },
                    ],
                  ],
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 4],
              },

              // Account Info
              {
                text: `Account: ${shop_name.value || '—'}`,
                style: 'account',
                margin: [0, 0, 0, 4],
              },
              {
                text: `Salesman: ${salesman_name.value} (${props.salesman_id})`,
                style: 'muted',
                margin: [0, 0, 0, 8],
              },

              // Opening Amount (Right aligned)
              {
                columns: [
                  { text: '', width: '*' },
                  {
                    text: `Opening Amount: ${opening_balance.value.toFixed(2)}`,
                    alignment: 'right',
                    style: 'openingAmount',
                    margin: [0, 0, 0, 6],
                  },
                ],
              },

              // Main Table
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [60, '*', 70, 70, 70, 80],
                  body: body.value,
                },
                layout: {
                  hLineColor: (i, node) =>
                    i === 0 || i === node.table.body.length ? '#999' : '#CCC',
                  vLineColor: () => '#EEE',
                  fillColor: (rowIndex) =>
                    rowIndex === 0 ? '#D1D5DB' : rowIndex % 2 === 0 ? '#FAFAFA' : null,
                },
              },
            ],
            styles: {
              tableHeader: { bold: true, fillColor: '#D1D5DB' },
              cell: { fontSize: 10, margin: [0, 3, 0, 3] },
              numberCell: { fontSize: 10, margin: [0, 3, 0, 3], alignment: 'right' },
              totalsLabel: { fontSize: 10, bold: true },
              title: { fontSize: 12, bold: true, color: '#111827' },
              subHeader: { fontSize: 10, color: '#111827' },
              account: { fontSize: 11, bold: true },
              muted: { fontSize: 10, color: '#6B7280' },
              openingAmount: { fontSize: 11, bold: true, color: '#2563EB' },
            },
          };
          pdfMake.vfs = pdfFonts;
          openPdf(pdfMake, documentDefinition.value, pdfFileName(['Shop-Ledger', shop_name.value], pdfDateRange(props.start_date, props.end_date)));
        });
    };

    watch(
      () => props.count_number,
      () => loadPdf()
    );

    return {
      pdfData,
      pdfStats,
      documentDefinition,
      body,
      salesman_name,
      total_debit,
      total_credit,
      total_return,
      opening_balance,
      closing_remaining,
    };
  },
};
</script>
